import { ApiConfigurationRule, EditFormData, Condition, NestedRuleGroup, NestedResponseGroup, UrlRule, ResponseRule, NestedConditionGroup } from './types';

export const mapOperatorFromApi = (op: string): string => {
  const operatorMap: { [key: string]: string } = {
    ew: 'ends with',
    sw: 'starts with',
    ct: 'contains',
    contains: 'contains',
    '!=': 'not equals',
    '==': 'equals',
    '>': 'greater than',
    '<': 'less than',
    '>=': 'greater than or equal',
    '<=': 'less than or equal',
  };
  return operatorMap[op] || op;
};

export const mapOperatorToApi = (operator: string): string => {
  const operatorMap: { [key: string]: string } = {
    'ends with': 'ew',
    'starts with': 'sw',
    contains: 'contains',
    'not equals': '!=',
    equals: '==',
    'equals to': '==',
    'not equal to': '!=',
    'greater than': '>',
    'less than': '<',
    'greater than or equal': '>=',
    'less than or equal': '<=',
  };
  return operatorMap[operator] || operator;
};

export const mapKeyFromApi = (key: string): string => {
  const keyMap: { [key: string]: string } = {
    rsc: 'responseCode',
    dur: 'duration',
    path: 'path',
    host: 'host',
    scheme: 'scheme',
  };
  return keyMap[key] || key;
};

export const mapKeyToApi = (key: string): string => {
  const keyMap: { [key: string]: string } = {
    responseCode: 'rsc',
    duration: 'dur',
    path: 'path',
    host: 'host',
    scheme: 'scheme',
  };
  return keyMap[key] || key;
};

const transformUrlRulesToApi = (rules: (UrlRule | NestedRuleGroup)[]): (Condition | NestedConditionGroup)[] => {
  return rules.map(rule => {
    if ('rules' in rule) {
      return {
        cond: transformUrlRulesToApi(rule.rules),
        type: rule.type,
      };
    } else {
      return {
        key: rule.type,
        op: mapOperatorToApi(rule.operator),
        val: rule.values,
      };
    }
  });
};

const transformResponseRulesToApi = (rules: (ResponseRule | NestedResponseGroup)[]): (Condition | NestedConditionGroup)[] => {
  return rules.map(rule => {
    if ('rules' in rule) {
      return {
        cond: transformResponseRulesToApi(rule.rules),
        type: rule.type,
      };
    } else {
      const apiKey = mapKeyToApi(rule.type);
      const value = rule.type === 'duration' || rule.type === 'responseCode'
        ? Number(rule.value)
        : rule.value;

      return {
        key: apiKey,
        op: mapOperatorToApi(rule.operator),
        val: value,
      };
    }
  });
};

const transformApiToUrlRules = (conditions: (Condition | NestedConditionGroup)[], baseId: number = Date.now()): (UrlRule | NestedRuleGroup)[] => {
  return conditions.map((condition, index) => {
    if ('cond' in condition && Array.isArray(condition.cond)) {
      return {
        id: (baseId + index).toString(),
        type: condition.type,
        rules: transformApiToUrlRules(condition.cond, baseId + 1000 + index * 100),
      } as NestedRuleGroup;
    } else {
      const cond = condition as Condition;
      if (!cond.key) {
        return null;
      }
      return {
        id: (baseId + index).toString(),
        type: cond.key as 'host' | 'path' | 'scheme',
        operator: mapOperatorFromApi(cond.op || '') as 'starts with' | 'contains' | 'ends with',
        values: Array.isArray(cond.val) ? cond.val.map(String) : [String(cond.val || '')],
      } as UrlRule;
    }
  }).filter(Boolean) as (UrlRule | NestedRuleGroup)[];
};

const transformApiToResponseRules = (conditions: (Condition | NestedConditionGroup)[], baseId: number = Date.now()): (ResponseRule | NestedResponseGroup)[] => {
  return conditions.map((condition, index) => {
    if ('cond' in condition && Array.isArray(condition.cond)) {
      return {
        id: (baseId + index).toString(),
        type: condition.type,
        rules: transformApiToResponseRules(condition.cond, baseId + 1000 + index * 100),
      } as NestedResponseGroup;
    } else {
      const cond = condition as Condition;
      if (!cond.key) {
        return null;
      }
      return {
        id: (baseId + index).toString(),
        type: mapKeyFromApi(cond.key) as 'duration' | 'responseCode' | 'attribute',
        operator: mapOperatorFromApi(cond.op || ''),
        value: String(cond.val || ''),
      } as ResponseRule;
    }
  }).filter(Boolean) as (ResponseRule | NestedResponseGroup)[];
};

export const transformFormDataToApi = (formData: EditFormData, ruleId?: string): ApiConfigurationRule => {
  // If forAllUrl is checked, use host == '*'
  const reqConditions = formData.forAllUrl 
    ? [{ key: 'host', op: '==', val: '*' }]
    : transformUrlRulesToApi(formData.urlRules);
  
  // Response rules are included regardless of forAllUrl state
  const optConditions = transformResponseRulesToApi(formData.responseRules);

  const apiRule: any = {
    id: ruleId || `temp-${Date.now()}`,
    rule: formData.allowBlock === 'collect' ? 'collect' : 'block',
    created_by: 'current-user@conviva.com',
    created_at: Math.floor(Date.now() / 1000),
    updated_at: Math.floor(Date.now() / 1000),
    req: {
      cond: reqConditions,
      type: formData.forAllUrl ? 'or' : formData.urlRuleOperator,
    },
  };

  // Always include response rules if present (even when forAllUrl is checked)
  if (formData.responseRules.length > 0 && optConditions.length > 0) {
    apiRule.opt = {
      cond: optConditions,
      type: formData.responseRuleOperator,
    };
  }

  return apiRule;
};

export const transformApiToFormData = (rule: ApiConfigurationRule): EditFormData => {
  // Check if this is a "For All URL" configuration (host == '*')
  const isForAllUrl = rule.req.cond.length === 1 && 
    'key' in rule.req.cond[0] &&
    rule.req.cond[0].key === 'host' && 
    rule.req.cond[0].op === '==' && 
    rule.req.cond[0].val === '*';
  
  const urlRules = isForAllUrl 
    ? [{
        id: Date.now().toString(),
        type: 'host' as const,
        operator: 'starts with' as const,
        values: [],
      }]
    : transformApiToUrlRules(rule.req.cond, Date.now());
  
  const responseRules = rule.opt && rule.opt.cond ? 
    transformApiToResponseRules(rule.opt.cond, Date.now() + 1000) : [];

  return {
    allowBlock: rule.rule === 'collect' ? 'collect' : 'block',
    forAllUrl: isForAllUrl,
    urlRuleOperator: rule.req.type,
    urlRules,
    responseRuleOperator: rule.opt?.type || 'and',
    responseRules,
  };
};

export const formatConditionsToMath = (conditions: (Condition | NestedConditionGroup)[], operator: 'and' | 'or'): string => {
  const formattedConditions = conditions.map((condition) => {
    if ('cond' in condition) {
      const nestedContent = formatConditionsToMath(condition.cond, condition.type);
      const hasMultipleAtThisLevel = conditions.length > 1;
      const hasDifferentOperator = condition.type !== operator;
      const needsParentheses = hasMultipleAtThisLevel && hasDifferentOperator;
      
      if (needsParentheses) {
        return `<span style="color: #1890ff; font-weight: bold;">( </span>${nestedContent}<span style="color: #1890ff; font-weight: bold;"> )</span>`;
      } else {
        return nestedContent;
      }
    } else {
      const { key, op, val } = condition;
      
      // Map abbreviated keys to full names for display
      const keyDisplayMap: { [key: string]: string } = {
        'dur': 'duration',
        'rsc': 'responseCode',
        'host': 'host',
        'path': 'path',
        'sch': 'scheme',
      };
      
      const displayKey = keyDisplayMap[key] || key;
      
      const operatorMap: { [key: string]: string } = {
        'ew': 'ends with',
        'sw': 'starts with', 
        'contains': 'contains',
        'ct': 'contains',
        '!=': '!=',
        '==': '=',
        '>': '>',
        '<': '<',
        '>=': '>=',
        '<=': '<='
      };
      
      const readableOp = operatorMap[op || ''] || op || '';
      
      const formatValueWithUnit = (value: any) => {
        const baseValue = typeof value === 'string' ? `'${value}'` : value.toString();
        if (key === 'dur') {
          return `${baseValue}ms`;
        }
        return baseValue;
      };

      if (Array.isArray(val)) {
        if (val.length === 1) {
          const formattedValue = formatValueWithUnit(val[0]);
          return `${displayKey} <span style="color: #52c41a; font-weight: bold;">${readableOp}</span> ${formattedValue}`;
        } else if (val.length > 1) {
          const expandedConditions = val.map(v => {
            const formattedValue = formatValueWithUnit(v);
            return `${displayKey} <span style="color: #52c41a; font-weight: bold;">${readableOp}</span> ${formattedValue}`;
          });
          return `<span style="color: #1890ff; font-weight: bold;">( </span>${expandedConditions.join('<span style="color: #52c41a; font-weight: bold;"> OR </span>')}<span style="color: #1890ff; font-weight: bold;"> )</span>`;
        }
      } else {
        const formattedValue = formatValueWithUnit(val);
        return `${displayKey} <span style="color: #52c41a; font-weight: bold;">${readableOp}</span> ${formattedValue}`;
      }
      
      return `${displayKey} <span style="color: #52c41a; font-weight: bold;">${readableOp}</span> ${val}`;
    }
  });
  
  return formattedConditions.join(`<span style="color: #52c41a; font-weight: bold;"> ${operator.toUpperCase()} </span>`);
};

export const formatRuleDisplay = (rule: ApiConfigurationRule): string => {
  const action = rule.rule === 'collect' ? 'Collect' : 'Block';
  
  let urlConditions = '';
  if (rule.req && rule.req.cond && rule.req.cond.length > 0) {
    urlConditions = formatConditionsToMath(rule.req.cond, rule.req.type);
  }
  
  let responseConditions = '';
  if (rule.opt && rule.opt.cond && rule.opt.cond.length > 0) {
    responseConditions = formatConditionsToMath(rule.opt.cond, rule.opt.type);
  }
  
  if (urlConditions && responseConditions) {
    return `${action} if ${urlConditions}<span style="color: #52c41a; font-weight: bold;"> AND </span><span style="color: #1890ff; font-weight: bold;">( </span>${responseConditions}<span style="color: #1890ff; font-weight: bold;"> )</span>`;
  } else if (urlConditions) {
    return `${action} if ${urlConditions}`;
  } else if (responseConditions) {
    return `${action} if ${responseConditions}`;
  }
  
  return `${action} if`;
};